import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Grid, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, OutlinedInput, MenuItem
} from '@mui/material';
import {
  Timeline, TimelineItem, TimelineSeparator, TimelineConnector,
  TimelineContent, TimelineDot
} from '@mui/lab';
import { Edit } from '@mui/icons-material';
import api from '../services/api';
import { acceptSample, updateSampleStatus } from '../store/sampleSlice';
import TimestampEditor from '../components/TimestampEditor';
import { formatToIST } from '../utils/dateUtils';

const SampleDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { showAllDates } = useSelector(state => state.settings);
  const [sample, setSample] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({ labId: '', sampleName: '', regularity: '', bisSubcategories: [], fssaiCategory: '', departments: [] });


  useEffect(() => {
    fetchSample();
  }, [id]);

  const fetchSample = async () => {
    try {
      const response = await api.get(`/samples/${id}`);
      setSample(response.data);
    } catch (error) {
      console.error('Error fetching sample:', error);
    }
  };

  const handleEdit = () => {
    setEditData({
      labId: sample.labId,
      sampleName: sample.sampleName,
      storageTemperature: sample.storageTemperature,
      regularity: sample.regularity,
      bisSubcategories: sample.bisSubcategories || [],
      fssaiCategory: sample.fssaiCategory || '',
      departments: sample.departments
    });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/samples/${id}`, editData);
      setEditDialog(false);
      fetchSample();
    } catch (error) {
      console.error('Error updating sample:', error);
    }
  };

  if (!sample) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Sample Details</Typography>
        {['Admin', 'Sample Collector'].includes(user?.role) && (
          <Button variant="outlined" startIcon={<Edit />} onClick={handleEdit}>
            Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Sample Information */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, lg: 0 } }}>
            <Typography variant="h6" gutterBottom>Sample Information</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">Lab ID</Typography>
                <Typography variant="body1">{sample.labId}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Sample Name</Typography>
                <Typography variant="body1">{sample.sampleName}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Regularity</Typography>
                <Chip 
                  label={sample.regularity || 'Not Set'} 
                  size="small" 
                  color={sample.regularity ? 'secondary' : 'default'}
                />
                {sample.regularity === 'BIS' && sample.bisSubcategories?.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    {sample.bisSubcategories.map(sub => (
                      <Chip key={sub} label={sub} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                )}
                {sample.regularity === 'FSSAI' && sample.fssaiCategory && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={sample.fssaiCategory} size="small" variant="outlined" />
                  </Box>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Storage Temperature</Typography>
                <Chip 
                  label={sample.storageTemperature || 'Not Set'} 
                  size="small" 
                  color={sample.storageTemperature ? 'primary' : 'default'}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Departments</Typography>
                <Box>
                  {sample.departments.map(dept => (
                    <Chip key={dept} label={dept} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </Box>
              {showAllDates && (
                <Box>
                  <Typography variant="body2" color="textSecondary">Created Date</Typography>
                  <Typography variant="body1">
                    {formatToIST(sample.createdDate)}
                  </Typography>
                </Box>
              )}
              {showAllDates && sample.departmentAssignments.some(da => da.hodAcceptedAt) && (
                <Box>
                  <Typography variant="body2" color="textSecondary">Accepted Date</Typography>
                  {sample.departmentAssignments
                    .filter(da => da.hodAcceptedAt)
                    .map(da => (
                      <Typography key={da.department} variant="body1" sx={{ mb: 0.5 }}>
                        {da.department}: {formatToIST(da.hodAcceptedAt)}
                      </Typography>
                    ))
                  }
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Department Status */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, lg: 0 } }}>
            <Typography variant="h6" gutterBottom>Department Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {sample.departmentAssignments.map(da => (
                <Box key={da.department} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{da.department}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>Status: <Chip label={da.status} size="small" /></Typography>
                  {da.hodAcceptedBy && (
                    <Typography variant="caption" display="block">HOD: {da.hodAcceptedBy.name}</Typography>
                  )}
                  {showAllDates && da.hodAcceptedAt && (
                    <Typography variant="caption" display="block" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                      Accepted: {formatToIST(da.hodAcceptedAt)}
                    </Typography>
                  )}

                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* History */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom>History</Typography>
            <Timeline sx={{ p: 0, m: 0 }}>
              {sample.history.map((event, index) => (
                <TimelineItem key={index} sx={{ minHeight: 'auto' }}>
                  <TimelineSeparator>
                    <TimelineDot size="small" />
                    {index < sample.history.length - 1 && <TimelineConnector sx={{ minHeight: 20 }} />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 0.5, px: 1 }}>
                    {user?.role === 'Admin' ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                          {event.eventType}
                          {event.extra?.department && ` (${event.extra.department})`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                          {event.by?.name || 'User'}{showAllDates ? ` - ${formatToIST(event.at || event.createdAt)}` : ''}
                        </Typography>
                        {event.note && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                            {event.note}
                          </Typography>
                        )}
                        {event.extra?.previousStatus && event.extra?.newStatus && (
                          <Typography variant="caption" color="primary" display="block" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                            {event.extra.previousStatus} → {event.extra.newStatus}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                        {showAllDates ? formatToIST(event.at || event.createdAt) : (event.eventType || 'Event')}
                      </Typography>
                    )}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Paper>
        </Grid>
      </Grid>

      {/* Admin Timestamp Editor - Only visible to Admin when dates are enabled */}
      {user?.role === 'Admin' && showAllDates && (
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3 }}>
            <TimestampEditor sample={sample} onUpdate={fetchSample} />
          </Paper>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Sample</DialogTitle>
        <DialogContent>
          <TextField
            label="Lab ID"
            value={editData.labId}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
            disabled
          />
          <TextField
            label="Sample Name"
            value={editData.sampleName}
            onChange={(e) => setEditData({ ...editData, sampleName: e.target.value })}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Departments</InputLabel>
            <Select
              multiple
              value={editData.departments}
              onChange={(e) => setEditData({ ...editData, departments: e.target.value })}
              input={<OutlinedInput label="Departments" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="Chemical">Chemical</MenuItem>
              <MenuItem value="Micro">Micro</MenuItem>
              <MenuItem value="Instrument">Instrument</MenuItem>
              <MenuItem value="GM">GM</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Storage Temperature</InputLabel>
            <Select
              value={editData.storageTemperature || ''}
              onChange={(e) => setEditData({ ...editData, storageTemperature: e.target.value })}
              input={<OutlinedInput label="Storage Temperature" />}
            >
              <MenuItem value="Ambient">Ambient</MenuItem>
              <MenuItem value="Freezed">Freezed</MenuItem>
              <MenuItem value="Deep Freezed">Deep Freezed</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Regularity</InputLabel>
            <Select
              value={editData.regularity || ''}
              onChange={(e) => setEditData({ ...editData, regularity: e.target.value, bisSubcategories: [], fssaiCategory: '' })}
              input={<OutlinedInput label="Regularity" />}
            >
              <MenuItem value="BIS">BIS</MenuItem>
              <MenuItem value="FSSAI">FSSAI</MenuItem>
              <MenuItem value="Food">Food</MenuItem>
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Subcontract">Subcontract</MenuItem>
              <MenuItem value="IQC">IQC</MenuItem>
              <MenuItem value="IOPEPC">IOPEPC</MenuItem>
            </Select>
          </FormControl>
          {editData.regularity === 'BIS' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>BIS Subcategories</InputLabel>
              <Select
                multiple
                value={editData.bisSubcategories}
                onChange={(e) => setEditData({ ...editData, bisSubcategories: e.target.value })}
                input={<OutlinedInput label="BIS Subcategories" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="Water">Water</MenuItem>
                <MenuItem value="Nox Reduction Agent">Nox Reduction Agent</MenuItem>
                <MenuItem value="Diaper">Diaper</MenuItem>
                <MenuItem value="Sanitary">Sanitary</MenuItem>
                <MenuItem value="FRK">FRK</MenuItem>
                <MenuItem value="Cattle Feed">Cattle Feed</MenuItem>
              </Select>
            </FormControl>
          )}
          {editData.regularity === 'FSSAI' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>FSSAI Category</InputLabel>
              <Select
                value={editData.fssaiCategory}
                onChange={(e) => setEditData({ ...editData, fssaiCategory: e.target.value })}
                input={<OutlinedInput label="FSSAI Category" />}
              >
                <MenuItem value="central">Central</MenuItem>
                <MenuItem value="import">Import</MenuItem>
                <MenuItem value="fbo">FBO</MenuItem>
                <MenuItem value="state">State</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SampleDetail;